import {Box, Text, Container, Flex} from '@chakra-ui/react';
import {GetStaticProps} from 'next';
import {ResultOf} from '@graphql-typed-document-node/core';
import {TitleMetaLinkTag} from 'react-datocms';
import {useUserQuery} from '../../graphql/generated';
import {PageLayout} from '../../components/global/page-layout';
import {FAQs, MARKETPLACE_FAQ_CONTENT} from '../../components/global/faq';
import {useGeolocation} from '../../hooks/geolocation';
import {MarketplacePageDocument} from '../../graphql/datocms-generated';
import {request} from '../../graphql/api';
import {
  ShowpieceCard,
  ShowpieceCardProps,
} from '../../components/showpiece-card/showpiece-card';
import {InfoAlert} from '../../components/global/info-alert';

interface MarketplaceProps {
  pageData: ResultOf<typeof MarketplacePageDocument>['marketplacePage'];
}

const Marketplace: React.FC<MarketplaceProps> = ({pageData}) => {
  const userQuery = useUserQuery();
  const {canBuyOnTheMarketplace, isLoadingGeolocation, countryCode} =
    useGeolocation();

  return (
    <PageLayout
      hideShapes
      firstName={userQuery.data?.user?.firstName}
      seo={pageData?.seo as TitleMetaLinkTag[]}
    >
      <Box>
        <Container
          width="100%"
          centerContent
          paddingBottom={{base: '32px', lg: '48px'}}
        >
          <Box paddingBottom={{base: '14px', lg: '8px'}}>
            <Text textStyle="h4">
              <Box
                as="span"
                display="inline-block"
                position="relative"
                px="15px"
              >
                Marketplace
                <Text
                  as="span"
                  position="absolute"
                  top="-5px"
                  right={0}
                  textStyle="showpiece_h8"
                  color="aqua.500"
                  letterSpacing="0.1px"
                >
                  BETA
                </Text>
              </Box>
            </Text>
          </Box>
          <Text
            textStyle={{base: 'body2', md: 'body1'}}
            px={{base: '24px', md: '0px'}}
            textAlign="center"
          >
            Buy Pieces from other collectors on the Showpiece Marketplace.
            <Text>You can sell pieces from My Collection. </Text>
          </Text>
        </Container>

        {(countryCode === 'US' ||
          (!canBuyOnTheMarketplace && !isLoadingGeolocation)) && (
          <InfoAlert
            title={
              countryCode === 'US'
                ? 'Sorry, Collectors based in the US are not currently able to purchase on the Showpiece platform'
                : 'Sorry, buying on the marketplace is not supported in your country.'
            }
            description={
              countryCode === 'US'
                ? 'If you have any questions, please contact hello@showpiece.com.'
                : 'Our international payments provider doesn’t currently operate in your region. You can still have ownership of your showpiece, though, and can share or gift from your collection. If you have any questions, please contact hello@showpiece.com.'
            }
            bgColor="aqua.500"
          />
        )}

        <Flex
          flexWrap="wrap"
          justifyContent="center"
          alignItems="stretch"
          paddingBottom={{base: '24px', lg: '40px'}}
          px={{base: '16px', md: '8px'}}
        >
          {pageData?.marketplaceCards.map(
            (card) =>
              card.cardTitle !== '' && (
                <Box
                  px={{base: 4}}
                  py={{base: 6}}
                  maxWidth={{
                    base: 'auto',
                    sm: '380px',
                    md: '400px',
                    lg: '426px',
                  }}
                  width="100%"
                  key={card.assetId}
                  display="block"
                >
                  <ShowpieceCard {...(card as ShowpieceCardProps)} />
                </Box>
              ),
          )}
        </Flex>

        <Box pt="160px" pb={100}>
          <FAQs faqs={MARKETPLACE_FAQ_CONTENT} />
        </Box>
      </Box>
    </PageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const pageData = await request(MarketplacePageDocument);

  return {
    revalidate: 10,
    props: {
      pageData: pageData.marketplacePage,
    },
  };
};

export default Marketplace;
